import React, { useState, useEffect } from 'react'
import {
  Box,
  Button,
  List,
  ListItem,
  ListItemText,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  IconButton,
  Alert,
  Checkbox,
  FormControlLabel,
  useTheme,
  Tooltip,
  Snackbar,
  useMediaQuery
} from '@mui/material'
import { DataGrid } from '@mui/x-data-grid'
import AddIcon from '@mui/icons-material/Add'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { useSelector } from 'react-redux'
import { toast } from 'react-hot-toast'
import {
  useCreateListMutation,
  useGetListsQuery,
  useGetListLeadsQuery,
  useDeleteListMutation,
  usePostRowsMutation,
  useAddLeadsfromListMutation,
  useGetDownloadListMutation
} from '../services/lists-service'
import { useGetCampaignsQuery } from '../services/campaign-service'
import { v4 as uuidv4 } from 'uuid'
import ConfirmationDialog from '../components/ConfirmationDialog'
import {
  Delete,
  Download
} from '@mui/icons-material'
import { downloadCsv } from 'src/utils/util.js'
import { ArrowLeftIconBlue } from 'src/assets/emailAccounts/connect/ArrowLeftIconBlue'
import { ArrowRight } from 'src/assets/general/ArrowRight'
import ListCsv from './ListCsv'
import { EACloseIcon } from 'src/assets/emailAccounts/EACloseIcon'
import { BulkUploadIcon } from 'src/assets/campaignDetailsLeads/BulkUploadIcon'
import ListDetailModal from 'src/components/leadsList/ListDetailModal.js'

const LeadsListView = () => {
  const [selectedList, setSelectedList] = useState(null)
  const [masterLists, setMasterLists] = useState(false)
  const [newListDialogOpen, setNewListDialogOpen] = useState(false)
  const [newListName, setNewListName] = useState('')
  const [selectedRows, setSelectedRows] = useState([])
  const [selectedRowData, setSelectedRowData] = useState([])
  const [addToCampaignDialogOpen, setAddToCampaignDialogOpen] = useState(false)
  const [selectedCampaign, setSelectedCampaign] = useState('')
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25
  })
  const [rowClickedId, setRowClickedId] = useState(null)
  const [isLeadDetailDrawerOpen, setIsLeadDetailDrawerOpen] = useState(false)
  const [isConfirmingLeads, setIsConfirmingLeads] = useState(false)
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false)
  const [deleteRowConfirmationOpen, setDeleteRowConfirmationOpen] = useState(false)
  const [leadDuplicate, setLeadDuplicate] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const storedWorkspace = JSON.parse(window.localStorage.getItem('workspace'))
  const [workspaceId, setWorkspaceId] = useState(storedWorkspace?._id || '')
  const theme = useTheme()
  const isMobile = useMediaQuery(theme?.breakpoints.down('md'))
  const [activeStep, setActiveStep] = useState(0)
  const [snackbarMsg, setSnackbarMsg] = useState('')
  const [snackbarOpen, setSnackbarOpen] = useState(false)

  const [isImportLeadsDialogOpen, setIsImportLeadsDialogOpen] = useState(false)

  const handleClickOpenImportLeadsDialog = () => {
    setIsImportLeadsDialogOpen(true)
  }

  const handleCloseOpenImportLeadsDialog = () => {
    setIsImportLeadsDialogOpen(false)
    setActiveStep(0)
  }

  const onLeadsCreate = (createdLeads) => {
    // eslint-disable-next-line
    if (createdLeads) setLeadCreated(true)
    setActiveStep(0)
    setIsImportLeadsDialogOpen(false)
  }
  // eslint-disable-next-line
  const [getDownloadList, { isLoading: isDownloadCSVLoading }] = useGetDownloadListMutation()
  // Get current workspace
  const currentWorkspace = useSelector((state) => state.workspace)
  useEffect(() => {
    if (currentWorkspace?._id) {
      setWorkspaceId(currentWorkspace._id)
    }
  }, [currentWorkspace])

  // Query parameters
  const buildQueryParams = (params) => {
    return Object.fromEntries(
      Object.entries(params).filter(
        ([_, value]) => value !== '' && value !== null && value !== undefined
      )
    )
  }

  const [limit, setLimit] = useState(10)
  const handleShowMoreOrLess = () => {
    if (limit >= (listsData?.totalDocs || 0)) {
      setLimit(10)
    } else {
      setLimit(prev => prev + 10)
    }
  }

  const listsQueryParams = searchTerm
    ? buildQueryParams({
      search: searchTerm,
      offset: 0,
      limit: paginationModel.pageSize,
      workspaceId
    })
    : { workspaceId, limit }

  const leadsQueryParams = selectedList
    ? {
        listId: selectedList,
        params: buildQueryParams({
          ...(searchTerm && { search: searchTerm }),
          offset: paginationModel.page * paginationModel.pageSize,
          limit: paginationModel.pageSize
        })
      }
    : null
  const {
    data: listsData,
    isLoading: listsLoading,
    refetch: refetchLists
  } = useGetListsQuery(listsQueryParams)
  // const selectedListData = listsData?.docs?.find((list) => list._id === selectedList)
  const {
    data: fetchedLeads = { docs: [] },
    isLoading: leadsLoading,
    refetch: refetchLeads
  } = useGetListLeadsQuery(leadsQueryParams, {
    skip: !selectedList
  })

  const [selectedListLeads, setSelectedListLeads] = useState([])
  useEffect(() => {
    if (fetchedLeads?.docs) {
      setSelectedListLeads(fetchedLeads)
    }
  }, [fetchedLeads])

  const exactTotalCount = selectedListLeads?.total || 0

  const { data: campaigns } = useGetCampaignsQuery({
    unibox: true,
    option: true,
    workspaceId
  })

  // Mutations
  const [createList, { isLoading: isCreating }] = useCreateListMutation()
  const [deleteList] = useDeleteListMutation()
  const [postRows] = usePostRowsMutation()
  // const [addLeadsToList] = useAddLeadsToListMutation()
  const [addLeadsfromList] = useAddLeadsfromListMutation()

  const lists = listsData?.docs || []

  const columns = [
    {
      field: 'name',
      headerName: 'Name',
      width: 200,
      valueGetter: (params) => `${params.row.first_name || ''} ${params.row.last_name || ''}`
    },
    {
      field: 'company',
      headerName: 'Company',
      width: 200,
      valueGetter: (params) => params.row.company || 'N/A'
    },
    { field: 'email', headerName: 'Email', width: 250 },
    { field: 'title', headerName: 'Title', width: 200 },
    { field: 'country', headerName: 'Country', width: 150 },
    { field: 'state', headerName: 'State', width: 150 },
    { field: 'city', headerName: 'City', width: 150 }
  ]

  const handleSearch = (event) => {
    setSearchTerm(event.target.value)
    setPaginationModel({ ...paginationModel, page: 0 })
  }

  const handleNewList = async () => {
    try {
      const newListNameStr = String(newListName || '')
      if (newListNameStr.length > 45) {
        toast.error('List name cannot exceed 45 characters')
        return
      }
      await createList({
        name: newListName,
        workspaceId
      }).unwrap()
      toast.success('The new list has been created successfully.')
      setNewListDialogOpen(false)
      setNewListName('')
      refetchLists()
    } catch (error) {
      toast.error(error.data?.error?.message || 'Failed to create list')
    }
  }

  const handleDeleteConfirmation = () => {
    setDeleteConfirmationOpen(true)
  }

  const handleRowDeleteConfirmation = () => {
    setDeleteRowConfirmationOpen(true)
  }

  const handleConfirmedDelete = async () => {
    try {
      await deleteList(selectedList).unwrap()
      toast.success('The list has been deleted successfully.')
      setSelectedList(null)
      setDeleteConfirmationOpen(false)
      refetchLists()
      refetchLeads()
      setSelectedListLeads([])
    } catch (error) {
      toast.error(error.data?.error?.message || 'Failed to delete list')
    }
  }

  const handleConfirmedPostRow = async () => {
    if (!selectedList || !selectedRows) {
      toast.error('Both selectedList and selectedRows are required.')
      return
    }
    try {
      const response = await postRows({ selectedList, selectedRows }).unwrap()
      toast.success(response?.deletedRows?.acknowledged ? 'Rows Deleted successfully' : '')
      refetchLeads()
      setDeleteRowConfirmationOpen(false)
    } catch (error) {
      toast.error(error.data?.error?.message || 'Failed to post rows')
    }
  }

  const handleAddToCampaign = async () => {
    try {
      const batchId = uuidv4()
      await addLeadsfromList({
        campaignId: selectedCampaign,
        leads: selectedRowData,
        checkDuplicates: leadDuplicate,
        workspaceId,
        batchId
      }).unwrap()

      setAddToCampaignDialogOpen(false)
      setSelectedCampaign('')
      refetchLeads()
      refetchLists()
      toast.loading(`${selectedRows.length} Leads Adding to campaign`)
    } catch (error) {
      toast.error(error.data?.error?.message || 'Failed to add leads to campaign')
    }
  }
  const handleDownloadSelectedLeads = async (selectRow) => {
    if (selectRow) {
      try {
        const filteredData = await getDownloadList(selectedList).unwrap()
        downloadCsv('listData', filteredData?.data, null)
      } catch (error) {
        console.error('Error fetching download list:', error)
      }
    } else {
      const excludedFields = [
        '_id',
        'workspaceId',
        '__v',
        'batchId',
        'createdAt',
        'csvId',
        'deleted',
        'list',
        'queryHash',
        'updatedAt'
      ]

      const filteredData = selectedRowData.map(row => {
        const cleaned = { ...row }
        excludedFields.forEach(field => delete cleaned[field])
        return cleaned
      })

      if (filteredData.length === 0) {
        return
      }

      downloadCsv('listData', filteredData, null)
    }
  }

  const handleLeadDetailDrawerClose = () => {
    setIsLeadDetailDrawerOpen(false)
    setRowClickedId(null)
  }

  const dataGridProps = {
    rows: selectedListLeads?.docs || [],
    columns,
    checkboxSelection: true,
    onRowSelectionModelChange: (newSelectedIds) => {
      setSelectedRows(newSelectedIds)
      const selectedData = (selectedListLeads?.docs || []).filter(row => newSelectedIds.includes(row._id))
      setSelectedRowData(selectedData)
    },
    rowSelectionModel: selectedRows,
    loading: leadsLoading,
    pagination: true,
    paginationMode: 'server',
    pageSizeOptions: [25, 50, 100],
    paginationModel: {
      page: paginationModel.page,
      pageSize: paginationModel.pageSize
    },
    rowCount: exactTotalCount,
    onPaginationModelChange: (newModel) => {
      setPaginationModel(newModel)
    },
    getRowId: (row) => row._id,
    onRowClick: (params) => {
      setRowClickedId(params.row._id)
      setIsLeadDetailDrawerOpen(true)
    },
    disableRowSelectionOnClick: true,
    slots: {
      pagination: (props) => {
        if (!selectedListLeads?.docs?.length) return null
        const currentPage = paginationModel.page
        const pageSize = paginationModel.pageSize
        const from = exactTotalCount === 0 ? 0 : currentPage * pageSize + 1
        const to = Math.min((currentPage + 1) * pageSize, exactTotalCount)
        return (
          <Box
            sx={{
              p: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderTop: '1px solid #E4E4E5'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant='body2' color='text.secondary'>
                Rows per page:
              </Typography>
              <Select
                value={pageSize}
                onChange={(e) => {
                  setPaginationModel((prev) => ({
                    ...prev,
                    pageSize: Number(e.target.value),
                    page: 0
                  }))
                }}
                size='small'
                sx={{ minWidth: 50, mr: 1 }}
              >
                {[25, 50, 100].map((size) => (
                  <MenuItem key={size} value={size}>
                    {size}
                  </MenuItem>
                ))}
              </Select>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant='body2' color='text.secondary'>
                {`${from}-${to} of ${exactTotalCount}`}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <IconButton
                  onClick={() => setPaginationModel((prev) => ({ ...prev, page: prev.page - 1 }))}
                  disabled={currentPage === 0}
                >
                  <ChevronLeftIcon />
                </IconButton>
                <IconButton
                  onClick={() => setPaginationModel((prev) => ({ ...prev, page: prev.page + 1 }))}
                  disabled={to >= exactTotalCount}
                >
                  <ChevronRightIcon />
                </IconButton>
              </Box>
            </Box>
          </Box>
        )
      },
      noRowsOverlay: () => (
        <Box sx={{ p: 2, textAlign: 'center' }}>
          {leadsLoading
            ? (
              <CircularProgress size={24} />
              )
            : selectedList
              ? (
                  'No leads found'
                )
              : (
                  'Select a list to view leads'
                )}
        </Box>
      )
    }
    // hideFooterSelectedRowCount: true,
  }

  return (
    <Box
      sx={{
        display: 'flex',
        height: '100vh',
        width: '100%',
        backgroundColor: '#F8FAFC'
      }}
    >
      <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
        {/* Left Sidebar */}
        <Box
          sx={{
            width: 280,
            bgcolor: 'white',
            borderRight: '1px solid #E4E4E5',
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            position: 'sticky',
            top: 0,
            height: '100vh',
            overflowY: 'auto',
            flexShrink: 0
          }}
        >
          {/* Sidebar content... */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2
            }}
          >
            <Typography
              sx={{
                fontSize: '16px',
                fontWeight: 700,
                color: theme.palette.primary.primaryText
              }}
            >
              Lists
            </Typography>
            <IconButton
              onClick={() => setNewListDialogOpen(true)}
              size='small'
              sx={{ color: theme.palette.primary.main }}
            >
              <AddIcon />
            </IconButton>
          </Box>

          <TextField
            placeholder='Search lists...'
            value={searchTerm}
            onChange={handleSearch}
            variant='outlined'
            size='small'
            sx={{ mb: 2 }}
          />

          {listsLoading
            ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress size={24} />
              </Box>
              )
            : lists?.length === 0
              ? (
                <Alert severity='info' sx={{ mt: 2 }}>
                  No lists found
                </Alert>
                )
              : (
                <List sx={{ pt: 0 }}>
                  {lists?.map((list) => (
                    <ListItem
                      key={list._id}
                      onClick={() => {
                        setSelectedList(list?._id)
                        setMasterLists(list?.masterLists)
                      }}
                      sx={{
                        borderRadius: 1,
                        mb: 1,
                        cursor: 'pointer',
                        bgcolor: selectedList === list._id ? '#F2F4F6' : 'transparent',
                        '&:hover': {
                          bgcolor: '#F2F4F6'
                        }
                      }}
                    >
                      <ListItemText
                        primary={
                          <Typography
                            sx={{
                              fontSize: '14px',
                              fontWeight: 500,
                              color: theme.palette.primary.primaryText
                            }}
                          >
                            {list.name}
                          </Typography>
                    }
                        secondary={
                      selectedList === list?._id && (
                        <Typography
                          sx={{
                            fontSize: '13px',
                            color: '#8181B0'
                          }}
                        >
                          {`${selectedListLeads?.total || 0} leads`}
                        </Typography>
                      )
                    }
                      />
                      <ChevronRightIcon sx={{ color: '#8181B0' }} />
                    </ListItem>
                  ))}
                </List>
                )}
          {/* listsData?.totalDocs */}
          {(listsData?.totalDocs > 10) && (
            <Button variant='outlined' onClick={handleShowMoreOrLess}>
              {limit >= listsData?.totalDocs ? 'Show less' : 'Show more'}
            </Button>
          )}

        </Box>

        {/* Main Content */}
        <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 3 }}>
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 3
            }}
          >
            <Typography
              sx={{
                fontSize: '20px',
                fontWeight: 700,
                color: theme.palette.primary.primaryText
              }}
            >
              {selectedList ? lists?.find((l) => l._id === selectedList)?.name : 'Select a List'}
            </Typography>
            <Dialog
              open={isImportLeadsDialogOpen}
              onClose={handleCloseOpenImportLeadsDialog}
              fullWidth
              maxWidth='md'
              sx={{ backgroundColor: 'rgba(4, 4, 30, 0.5)' }}
              fullScreen={isMobile}
            >
              <DialogTitle>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    width: '100%'
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'flex-start',
                      flexDirection: 'column'
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                      <Typography
                        sx={{
                          fontSize: '20px',
                          fontWeight: 700,
                          lineHeight: '28px',
                          color: `${theme?.palette.primary.primaryText}`
                        }}
                      >
                        Import Leads
                      </Typography>
                      {activeStep !== 0 && (
                        <>
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              mr: 0.5
                            }}
                          >
                            <ArrowRight />
                          </Box>
                        </>
                      )}
                      {activeStep === 1
                        ? (
                          <>
                            <Typography
                              sx={{
                                fontSize: '16px',
                                fontWeight: 700,
                                lineHeight: '28px',
                                color: '#8181B0'
                              }}
                            >
                              Import CSV File
                            </Typography>
                          </>
                          )
                        : activeStep === 2
                          ? (
                            <>
                              <Typography
                                sx={{
                                  fontSize: '16px',
                                  fontWeight: 700,
                                  lineHeight: '28px',
                                  color: '#8181B0'
                                }}
                              >
                                Use Lead Finder
                              </Typography>
                            </>
                            )
                          : activeStep === 3
                            ? (
                              <>
                                <Typography
                                  sx={{
                                    fontSize: '16px',
                                    fontWeight: 700,
                                    lineHeight: '28px',
                                    color: '#8181B0'
                                  }}
                                >
                                  Input Emails Manually
                                </Typography>
                              </>
                              )
                            : activeStep === 4
                              ? (
                                <>
                                  <Typography
                                    sx={{
                                      fontSize: '16px',
                                      fontWeight: 700,
                                      lineHeight: '28px',
                                      color: '#8181B0'
                                    }}
                                  >
                                    Utilize Google Sheets
                                  </Typography>
                                </>
                                )
                              : null}
                    </Box>

                    {activeStep === 0
                      ? (
                        <>
                          <Typography
                            sx={{
                              fontSize: '13px',
                              fontWeight: 400,
                              lineHeight: '20px',
                              color: '#8181B0',
                              mt: 1
                            }}
                          >
                            Choose one of the methods listed below to effortlessly import leads.
                          </Typography>
                        </>
                        )
                      : (
                        <>
                          <Typography
                            sx={{
                              fontSize: '13px',
                              fontWeight: 400,
                              lineHeight: '20px',
                              color: '#8181B0',
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              mt: 1,
                              cursor: 'pointer'
                            }}
                            onClick={() => {
                              setActiveStep(0)
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                mr: 1
                              }}
                            >
                              <ArrowLeftIconBlue color='#8181B0' />
                            </Box>
                            Select a Different Method
                          </Typography>
                        </>
                        )}
                  </Box>
                  <IconButton
                    sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                    onClick={handleCloseOpenImportLeadsDialog}
                  >
                    <EACloseIcon />
                  </IconButton>
                </Box>
              </DialogTitle>
              <DialogContent>
                {activeStep === 0
                  ? (
                    <>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          flexDirection: { xs: 'column', md: 'row' },
                          rowGap: 2
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: { xs: 'center', md: 'flex-start' },
                            cursor: 'pointer',
                            width: { xs: '70%', sm: '40%', md: '100%' },
                            p: 3,
                            border: '1px solid #00AA38',
                            flexDirection: 'column',
                            height: '100%',
                            backgroundColor: '#fff',
                            borderRadius: '12px',
                            boxShadow: '0px 12px 15px 0px #4B71970D'
                          }}
                          onClick={() => {
                            setActiveStep(1)
                          }}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <BulkUploadIcon />
                          </Box>

                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'flex-start'
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: '14px',
                                fontWeight: 700,
                                lineHeight: '26px',
                                color: `${theme?.palette.primary.primaryText}`,
                                mt: 1.5
                              }}
                            >
                              Upload CSV
                            </Typography>
                          </Box>
                        </Box>

                      </Box>
                    </>
                    )
                  : activeStep === 1
                    ? (
                      <ListCsv
                        selectedList={selectedList}
                        onLeadsCreate={onLeadsCreate}
                        setSnackbarMsg={setSnackbarMsg}
                        setSnackbarOpen={setSnackbarOpen}
                        setIsImportLeadsDialogOpen={setIsImportLeadsDialogOpen}
                        exactTotalCount={selectedListLeads?.total}
                        refetchLeads={refetchLeads}
                        refetchLists={refetchLists}
                      />
                      )
                    : null}
              </DialogContent>
            </Dialog>
            <Box sx={{ display: 'flex', gap: 2 }}>
              {selectedRows && selectedRows.length > 0 && !masterLists && (
                <IconButton onClick={handleRowDeleteConfirmation}>
                  <Delete sx={{ color: 'red' }} fontSize='small' />
                </IconButton>
              )}
              {selectedList && !selectedRows.length > 0 && !masterLists && (
                <IconButton onClick={handleDeleteConfirmation}>
                  <Delete sx={{ color: 'red' }} fontSize='small' />
                </IconButton>
              )}
              {selectedList && !selectedRows.length > 0 && selectedListLeads?.docs.length > 0 && !masterLists && (
                <Tooltip title='Download the Selected Leads' arrow placement='top'>
                  <IconButton onClick={() => handleDownloadSelectedLeads(true)}>
                    <Download sx={{ color: 'rgba(0,0,0,0.6)' }} fontSize='small' />
                  </IconButton>
                </Tooltip>
              )}

              {selectedRows.length > 0
                ? (
                  <Tooltip title='Download the Selected Leads' arrow placement='top'>
                    <IconButton disabled={selectedRows.length === 0} onClick={() => handleDownloadSelectedLeads(false)}>
                      <Download sx={{ color: 'rgba(0,0,0,0.6)' }} fontSize='small' />
                    </IconButton>
                  </Tooltip>
                  )
                : (<></>)}
              {selectedList && !selectedRows.length > 0 && !masterLists && (
                <Button
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    '&:hover': {
                      backgroundColor: theme?.palette.primary.dark,
                      boxShadow: 10
                    },
                    textAlign: 'left',
                    fontSize: '14px',
                    fontWeight: 700,
                    lineHeight: '18px',
                    letterSpacing: '0em',
                    color: 'white',
                    backgroundColor: `${theme?.palette.primary.main}`,
                    borderRadius: '8px',
                    py: 1.2,
                    px: 2
                  }}
                  disabled={selectedListLeads?.total >= 10000}
                  variant='contained'
                  size='large'
                  onClick={handleClickOpenImportLeadsDialog}
                >
                  Import
                </Button>
              )}
              <Button
                variant='contained'
                onClick={() => setAddToCampaignDialogOpen(true)}
                disabled={selectedRows.length === 0}
                sx={{
                  bgcolor: theme.palette.primary.main,
                  '&:hover': {
                    bgcolor: '#1b5cc8'
                  }
                }}
              >
                Add to Campaign
              </Button>
            </Box>
          </Box>

          {/* DataGrid Box */}
          <Box
            sx={{
              bgcolor: 'white',
              borderRadius: 2,
              boxShadow: '0px 1px 3px rgba(16, 24, 40, 0.1), 0px 1px 2px rgba(16, 24, 40, 0.06)',
              height: 'calc(100% - 60px)'
            }}
          >
            <DataGrid
              {...dataGridProps}
              sx={{
                border: 'none',
                '& .MuiDataGrid-cell': {
                  borderBottom: '1px solid #E4E4E5'
                },
                '& .MuiDataGrid-columnHeaders': {
                  bgcolor: '#F2F4F6',
                  borderBottom: '1px solid #E4E4E5'
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                  fontWeight: 700,
                  color: theme.palette.primary.primaryText
                }
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* New List Dialog */}
      <Dialog
        open={newListDialogOpen}
        onClose={() => setNewListDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: 2,
            width: '100%',
            maxWidth: 480
          }
        }}
      >
        <DialogTitle
          sx={{
            fontSize: '20px',
            fontWeight: 700,
            color: theme.palette.primary.primaryText
          }}
        >
          Create New List
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin='dense'
            label='List Name'
            fullWidth
            value={newListName}
            onChange={(e) => setNewListName(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: '#E4E4E5'
                },
                '&:hover fieldset': {
                  borderColor: theme.palette.primary.main
                }
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setNewListDialogOpen(false)}
            variant='outlined'
            sx={{
              borderColor: '#E4E4E5',
              color: theme.palette.primary.primaryText,
              '&:hover': {
                borderColor: theme.palette.primary.main
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleNewList}
            disabled={!newListName.trim() || isCreating}
            variant='contained'
            sx={{
              bgcolor: theme.palette.primary.main,
              '&:hover': {
                bgcolor: '#1b5cc8'
              }
            }}
          >
            {isCreating ? <CircularProgress size={24} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteConfirmationOpen}
        onClose={() => setDeleteConfirmationOpen(false)}
        onConfirm={handleConfirmedDelete}
        title='Confirm Delete'
        content='Are you sure you want to delete this list?'
        confirmText='Delete'
        cancelText='Cancel'
        confirmColor='error'
      />

      <ConfirmationDialog
        open={deleteRowConfirmationOpen}
        onClose={() => setDeleteRowConfirmationOpen(false)}
        onConfirm={handleConfirmedPostRow}
        title='Confirm Delete'
        content="Are you sure you want to delete the selected lead's?"
        confirmText='Delete'
        cancelText='Cancel'
        confirmColor='error'
      />

      {/* Add to Campaign Dialog */}
      <Dialog
        open={addToCampaignDialogOpen}
        onClose={() => setAddToCampaignDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: 2,
            width: '100%',
            maxWidth: 480
          }
        }}
      >
        <DialogTitle
          sx={{
            fontSize: '20px',
            fontWeight: 700,
            color: theme.palette.primary.primaryText
          }}
        >
          Add to Campaign
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Select Campaign</InputLabel>
            <Select
              value={selectedCampaign}
              onChange={(e) => setSelectedCampaign(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#E4E4E5'
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.primary.main
                }
              }}
            >
              {campaigns?.updatedEmail?.map((campaign) => (
                <MenuItem key={campaign._id} value={campaign._id}>
                  {campaign.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {/* <FormControlLabel
            control={
              <Checkbox checked={verifyLeads} onChange={(e) => setVerifyLeads(e.target.checked)} />
            }
            label='Validate Emails'
            sx={{ mt: 2, display: 'block' }}
          /> */}
          <FormControlLabel
            control={
              <Checkbox
                checked={leadDuplicate}
                onChange={(e) => setLeadDuplicate(e.target.checked)}
              />
            }
            label='Check Duplicates'
            sx={{ mt: 1, display: 'block' }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setAddToCampaignDialogOpen(false)}
            variant='outlined'
            sx={{
              borderColor: '#E4E4E5',
              color: theme.palette.primary.primaryText,
              '&:hover': {
                borderColor: theme.palette.primary.main
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddToCampaign}
            disabled={!selectedCampaign}
            variant='contained'
            sx={{
              bgcolor: theme.palette.primary.main,
              '&:hover': {
                bgcolor: '#1b5cc8'
              }
            }}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
      {/* ListDetailModal when we have a selected lead */}
      {isLeadDetailDrawerOpen && (
        <ListDetailModal
          isOpen={isLeadDetailDrawerOpen}
          onClose={handleLeadDetailDrawerClose}
          user={selectedListLeads?.docs?.find((lead) => lead._id === rowClickedId) || null}
          handleAddToListClick={() => {
            handleLeadDetailDrawerClose()
            // list addition logic here
          }}
          handleAddToCampaignClick={() => {
            setIsConfirmingLeads(true)
            // Add the selected lead to the campaign
            const leadToAdd = selectedListLeads?.docs?.find((lead) => lead._id === rowClickedId)
            if (leadToAdd) {
              setSelectedRows([leadToAdd._id])
              setSelectedRowData([leadToAdd])
              handleLeadDetailDrawerClose()
              setAddToCampaignDialogOpen(true)
            }
            setIsConfirmingLeads(false)
          }}
          isConfirmingLeads={isConfirmingLeads}
        />
      )}
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={snackbarOpen}
        message={snackbarMsg}
        key='csv-import'
        ContentProps={{
          sx: {
            backgroundColor: theme?.palette.primary.main,
            color: theme?.palette.primary.contrastText
          }
        }}
      />
    </Box>
  )
}

export default LeadsListView
